/**
 * Менеджер прав доступа с поддержкой localStorage и миграции на бэкенд
 *
 * Архитектура:
 * 1. Сначала проверяет localStorage (для текущей реализации)
 * 2. В будущем легко мигрировать на проверку через API
 * 3. Поддерживает fallback на старую систему
 */

// Конфигурация прав по ролям (дублирует логику из Redux slice)
const createAccessConfig = (permissions) => ({
  actions: {
    orders: {
      ...permissions,
      order: { ...permissions },
    },
    contacts: {
      ...permissions,
      contact: { ...permissions },
    },
    messages: {
      ...permissions,
    },
    logistic: {
      ...permissions,
      driver: { ...permissions },
      route: { ...permissions },
      vehicle: { ...permissions },
    },
    catalog: {
      ...permissions,
      product: { ...permissions },
      productManager: { ...permissions },
      lowProfit: { ...permissions },
      SPOManagment: { ...permissions },
      personalRates: { ...permissions },
    },
    billings: {
      invoicing: { ...permissions },
      debtors: { ...permissions },
    },
    operations: {
      shopping: { ...permissions },
      buyer: { ...permissions },
      orderHistory: {
        ...permissions,
      },
      stock: { ...permissions },
      picking: { ...permissions },
    },
    suppliers: {
      juice: { ...permissions },
      cuartagamma: { ...permissions },
      coctail: { ...permissions },
      oriental: { ...permissions },
    },
    integration: {
      ...permissions,
    },
    mbApp: {
      ...permissions,
    },
  },
})

// Права для каждой роли
const ROLE_PERMISSIONS = {
  admin: createAccessConfig({
    canDelete: true,
    canEdit: true,
    canExport: true,
    canCopy: true,
    canView: true,
    canPrint: true,
    canCreate: true,
  }),
  user: createAccessConfig({
    canDelete: false,
    canEdit: false,
    canExport: true,
    canCopy: false,
    canView: true,
    canPrint: true,
    canCreate: false,
  }),
  driver: createAccessConfig({
    canDelete: false,
    canEdit: false,
    canExport: false,
    canCopy: false,
    canView: true,
    canPrint: true,
    canCreate: false,
  }),
  picker: createAccessConfig({
    canDelete: false,
    canEdit: false,
    canExport: false,
    canCopy: false,
    canView: true,
    canPrint: true,
    canCreate: false,
  }),
  viewer: createAccessConfig({
    canDelete: false,
    canEdit: false,
    canExport: false,
    canCopy: false,
    canView: false,
    canPrint: false,
    canCreate: false,
  }),
}

/**
 * Получает данные пользователя из localStorage
 * @returns {Object} { role, permissions, userData }
 */
export const getUserDataFromStorage = () => {
  try {
    const accData = localStorage.getItem('acc')
    if (!accData) {
      return {
        role: 'viewer',
        permissions: null,
        userData: null,
      }
    }

    const userData = JSON.parse(accData)
    const role = userData.role || 'viewer'

    // Если права пришли с бэкенда, используем их
    // Иначе генерируем по роли
    const permissions = userData.actions
      ? { actions: userData.actions }
      : ROLE_PERMISSIONS[role]

    return {
      role,
      permissions,
      userData,
    }
  } catch (error) {
    console.error(
      'Error parsing user data from localStorage:',
      error,
    )
    return {
      role: 'viewer',
      permissions: null,
      userData: null,
    }
  }
}

/**
 * Проверяет есть ли у пользователя конкретное право
 * @param {string} module - модуль (orders, contacts, etc.)
 * @param {string} action - действие (canView, canEdit, etc.)
 * @param {string} subModule - подмодуль (опционально)
 * @returns {boolean}
 */
export const hasPermission = (
  module,
  action,
  subModule = null,
) => {
  const { permissions } = getUserDataFromStorage()

  if (!permissions) {
    return false
  }

  // Поддержка как новой структуры (permissions.actions), так и старой (permissions напрямую)
  const permissionsData =
    (permissions && permissions.actions) || permissions
  if (!permissionsData) {
    return false
  }

  const modulePermissions = permissionsData[module]
  if (!modulePermissions) {
    return false
  }

  // Если указан подмодуль
  if (subModule) {
    const subModulePermissions =
      modulePermissions[subModule]
    if (!subModulePermissions) {
      return false
    }
    return subModulePermissions[action] === true
  }

  // Проверка основного модуля
  return modulePermissions[action] === true
}

/**
 * Проверяет роль пользователя
 * @param {string} role - роль для проверки
 * @returns {boolean}
 */
export const hasRole = (role) => {
  const { role: userRole } = getUserDataFromStorage()
  return userRole === role
}

/**
 * Проверяет является ли пользователь админом
 * @returns {boolean}
 */
export const isAdmin = () => {
  return hasRole('admin')
}

/**
 * Получает все права пользователя
 * @returns {Object} permissions object
 */
export const getUserPermissions = () => {
  const { permissions } = getUserDataFromStorage()
  return permissions
}

/**
 * Получает роль пользователя
 * @returns {string} user role
 */
export const getUserRole = () => {
  const { role } = getUserDataFromStorage()
  return role
}

/**
 * Проверяет авторизован ли пользователь
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const accData = localStorage.getItem('acc')
  return !!accData
}

/**
 * Устанавливает права пользователя в localStorage
 * Используется при логине или обновлении прав
 * @param {Object} userData - данные пользователя с правами
 */
export const setUserPermissions = (userData) => {
  try {
    localStorage.setItem('acc', JSON.stringify(userData))
  } catch (error) {
    console.error('Error setting user permissions:', error)
  }
}

/**
 * Очищает права пользователя
 * Используется при логауте
 */
export const clearUserPermissions = () => {
  localStorage.removeItem('acc')
  localStorage.removeItem('name')
  localStorage.removeItem('password')
}

/**
 * Проверяет доступ к роуту на основе прав
 * @param {string} requiredPermission - требуемое право (например, 'orders.canView' или 'operations.shipment.canView')
 * @returns {boolean}
 */
export const canAccessRoute = (requiredPermission) => {
  if (!isAuthenticated()) {
    return false
  }

  // Админы имеют доступ ко всему
  if (isAdmin()) {
    return true
  }

  // Парсим требуемое право
  const parts = requiredPermission.split('.')
  if (parts.length < 2) {
    console.warn(
      `Invalid permission format: ${requiredPermission}`,
    )
    return false
  }

  // Если это подмодуль (например, operations.shipment.canView)
  if (parts.length === 3) {
    const [module, subModule, action] = parts
    return hasPermission(module, action, subModule)
  }

  // Если это основной модуль (например, orders.canView)
  const [module, action] = parts
  return hasPermission(module, action)
}

/**
 * Получает список доступных роутов для пользователя
 * @returns {Array} массив доступных роутов
 */
export const getAvailableRoutes = () => {
  const routes = [
    { path: '/admin/orders', permission: 'orders.canView' },
    {
      path: '/admin/contacts',
      permission: 'contacts.canView',
    },
    {
      path: '/admin/catalog',
      permission: 'catalog.canView',
    },
    {
      path: '/admin/logistic',
      permission: 'logistic.canView',
    },
    {
      path: '/admin/messages',
      permission: 'messages.canView',
    },
    {
      path: '/admin/shipment',
      permission: 'operations.picking.canView',
    },
    {
      path: '/admin/picking',
      permission: 'operations.picking.canView',
    },
    {
      path: '/admin/buyer',
      permission: 'operations.buyer.canView',
    },
    {
      path: '/admin/stock',
      permission: 'operations.stock.canView',
    },
    {
      path: '/admin/shopping',
      permission: 'operations.shopping.canView',
    },
    {
      path: '/admin/orderHistory',
      permission: 'operations.orderHistory.canView',
    },
    {
      path: '/admin/billings',
      permission: 'billings.invoicing.canView',
    },
    {
      path: '/admin/debtors',
      permission: 'billings.debtors.canView',
    },
    {
      path: '/admin/suppliers',
      permission: 'suppliers.juice.canView',
    },
    {
      path: '/admin/integration',
      permission: 'integration.canView',
    },
    { path: '/admin/MBApp', permission: 'mbApp.canView' },
    {
      path: '/admin/productManager',
      permission: 'catalog.productManager.canView',
    },
    {
      path: '/admin/lowProfit',
      permission: 'catalog.lowProfit.canView',
    },
    {
      path: '/admin/SPOManagment',
      permission: 'catalog.SPOManagment.canView',
    },
    {
      path: '/admin/personalRates',
      permission: 'catalog.personalRates.canView',
    },
  ]

  try {
    return routes.filter((route) =>
      canAccessRoute(route.permission),
    )
  } catch (error) {
    console.error('Error in getAvailableRoutes:', error)
    return []
  }
}

/**
 * Генерирует права по роли (для миграции)
 * @param {string} role - роль пользователя
 * @returns {Object} permissions object
 */
export const generatePermissionsByRole = (role) => {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer
}

// Экспорт для совместимости с будущим API
export const permissionsAPI = {
  // В будущем здесь будут методы для работы с API
  // getUserPermissions: async () => { ... },
  // checkPermission: async (permission) => { ... },
  // updatePermissions: async (permissions) => { ... },
}

export default {
  getUserDataFromStorage,
  hasPermission,
  hasRole,
  isAdmin,
  getUserPermissions,
  getUserRole,
  isAuthenticated,
  setUserPermissions,
  clearUserPermissions,
  canAccessRoute,
  getAvailableRoutes,
  generatePermissionsByRole,
  permissionsAPI,
}
